import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Paper,
  Typography
} from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid';
import { HOUR_IN_MILLISECONDS, PAGINATION_DEFAULT_PAGE_SIZE } from 'config';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import { useProject } from 'modules/project/providers/project';
import {
  InspectionPointDto,
  InspectionPointMutation,
  RETICULATION_DATA_EXPORT_KEY,
  refreshMineModel,
  useAddInspectionPoint,
  useAssignInspectionPointSpoolMutation,
  useBulkUpdateSpoolsSilentMutation,
  useDeleteInspectionPointMutation,
  useSearchInspectionPointsQuery,
  useSearchNodeVolumeRecordsQuery,
  useSearchWearDataQuery
} from 'providers/api';
import { Permission, useAuthorisation } from 'providers/authorisation';
import { useConfirmation } from 'providers/confirm';
import { usePage } from 'providers/page';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ProjectAreaPageParams } from '../../constants';
import InspectionPointsForm from './InspectionPointsForm';
import InspectionPointsTable from './InspectionPointsTable';
import inspectionPointDisplayName from './inspectionPointName';
import SpoolForm from '../SpoolForm';
import { Model } from '../hooks/useModelState';
import { generatePipeColour, spoolTonnesById, useCalculateWearPerPipe } from '../canvas/PCFunctions/wearCalculations';
import { SPOOL_COLOUR } from '../MineModel/PCConstants';
import { describeSpool } from '../MineModel/Spools';
import {
  useSceneLegend,
  generatePipeTypeLegend,
  generateWearRateLegend,
  generateThroughputLegend,
  generateRemainingThicknessLegend,
  generateRemainingPercentageLegend,
  PipeDisplayMode
} from '../Components/SceneLegend';

interface WearMonitoringPanelProps {
  model: Model;
  wearDataViewPath: string;
  onInspectionPointSelect?: (inspectionPointId: string) => void;
}

const ALL_RECORDS_PAGE_SIZE = 1000;

const WearMonitoringPanelPage = ({ model, wearDataViewPath, onInspectionPointSelect }: WearMonitoringPanelProps) => {
  const confirm = useConfirmation();
  const navigate = useNavigate();
  const { entityId: projectId } = useParams<ProjectAreaPageParams>();
  const {
    actions: {
      setUndoActionOverride,
      clearUndoActionOverride,
    },
  } = usePage();
  const queryClient = useQueryClient();
  const { state: { clientId } } = useProject();
  const { functions: { validatePermission } } = useAuthorisation();
  const canEditSpools = validatePermission(Permission.ManageNodeData, [projectId!, clientId]);
  const deleteInspectionPointMutation = useDeleteInspectionPointMutation();
  const addInspectionPointMutation = useAddInspectionPoint();
  const assignInspectionPointSpoolMutation = useAssignInspectionPointSpoolMutation(projectId!);
  const bulkUpdateSpoolsMutation = useBulkUpdateSpoolsSilentMutation(projectId!);
  const [spoolToEditId, setSpoolToEditId] = React.useState<string>();
  const lastCreatedInspectionPointIdRef = React.useRef<string | null>(null);
  const appliedInspectionPointSignatureRef = React.useRef<string>('');
  const batchUpdateSpoolAppearanceRef = React.useRef(model.batchUpdateSpoolAppearance);
  const setSpoolInspectionPointsRef = React.useRef(model.setSpoolInspectionPoints);
  const { setLegendData, setShowLegend } = useSceneLegend();
  const [pipeDisplayMode, setPipeDisplayMode] = React.useState<PipeDisplayMode>(null);

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(PAGINATION_DEFAULT_PAGE_SIZE);
  const [duplicateWarning, setDuplicateWarning] = React.useState<string | null>(null);

  // The selection is a live model spool: the scene stores whatever instance was clicked, and the
  // model's map is where the inspection flags and colours are kept up to date.
  const selectedSpool = model.selectedSpool ? (model.spools[model.selectedSpool.spoolId] ?? model.selectedSpool) : undefined;
  const selectedSpoolLabel = selectedSpool ? describeSpool(selectedSpool) : undefined;

  const { data, isFetching, isFetched, isPreviousData } = useSearchInspectionPointsQuery({
    projectId: projectId!,
    page,
    pageSize,
  });

  const { data: throughputRecords } = useSearchNodeVolumeRecordsQuery(
    {
      mineModelId: projectId!,
      page: 1,
      pageSize: ALL_RECORDS_PAGE_SIZE,
    },
    {
      enabled: !!projectId,
    },
  );

  const wearDataQuery = useSearchWearDataQuery({
    mineModelId: projectId!,
    page: 1,
    pageSize: ALL_RECORDS_PAGE_SIZE,
    inspectionPoint: '',
  }, {
    staleTime: HOUR_IN_MILLISECONDS,
  });

  const spoolTonnes = React.useMemo(() => spoolTonnesById(Object.values(model.spools)), [model.spools]);

  const pipeWear = React.useMemo(() => {
    if (pipeDisplayMode === null || !throughputRecords?.items || !wearDataQuery.data) {
      return undefined;
    }
    return useCalculateWearPerPipe(true, data?.items, model, throughputRecords, wearDataQuery.data);
  }, [pipeDisplayMode, throughputRecords?.items, wearDataQuery.data, data?.items, model]);

  const describeInspectionPointSpool = (inspectionPoint: InspectionPointDto): string => {
    if (inspectionPoint.spoolId) {
      const spool = model.spools[inspectionPoint.spoolId];
      return spool ? describeSpool(spool) : i18n._('Spool no longer exists');
    }
    return `${i18n._('Was at node')} ${inspectionPoint.nodeId ?? '?'}`;
  };

  const resolveInspectionPointName = (inspectionPoint: InspectionPointDto): string => inspectionPointDisplayName(
    inspectionPoint,
    inspectionPoint.spoolId ? model.spools[inspectionPoint.spoolId]?.name : undefined,
  );

  const canEditInspectionPointSpool = (inspectionPoint: InspectionPointDto): boolean => (
    canEditSpools && !!inspectionPoint.spoolId && !!model.spools[inspectionPoint.spoolId]
  );

  // The point is named after its spool, so the spool is renamed first and the point is only added once that has saved.
  const handleAddInspectionPoint = async (values: InspectionPointMutation) => {
    const name = values.name.trim();
    if (canEditSpools && name !== (model.spools[values.spoolId]?.name ?? '')) {
      await bulkUpdateSpoolsMutation.mutateAsync({ projectId: values.projectId, spools: [{ spoolId: values.spoolId, name }] });
      refreshMineModel(queryClient);
      queryClient.invalidateQueries(RETICULATION_DATA_EXPORT_KEY, { exact: false });
    }
    addInspectionPointMutation.mutate({ ...values, name });
  };

  const publishUndoAddInspectionPoint = React.useCallback((inspectionPointId: string) => {
    const handleUndoAddInspectionPoint = () => {
      setUndoActionOverride((current) => (current ? {
        ...current,
        enabled: false,
        loading: true,
      } : current));

      deleteInspectionPointMutation.mutate(
        { projectId: projectId!, entityId: inspectionPointId },
        {
          onSuccess: () => {
            clearUndoActionOverride();
          },
          onError: () => {
            setUndoActionOverride({
              enabled: true,
              loading: false,
              onClick: handleUndoAddInspectionPoint,
            });
          },
        },
      );
    };

    setUndoActionOverride({
      enabled: true,
      loading: false,
      onClick: handleUndoAddInspectionPoint,
    });
  }, [clearUndoActionOverride, deleteInspectionPointMutation, projectId, setUndoActionOverride]);

  const getCurrentPipeDisplayMode = () => pipeDisplayMode;

  const handlePipeDisplayModeChange = (mode: typeof pipeDisplayMode) => {
    setPipeDisplayMode(mode);
  };

  const pipeColours = React.useMemo(() => {
    if (pipeDisplayMode === null) {
      return {};
    }
    return generatePipeColour(Object.values(model.spools), pipeDisplayMode, pipeWear);
  }, [pipeDisplayMode, model.spools, pipeWear]);

  const handlePaginationChange = (newPaginationState: GridPaginationModel) => {
    setPage(newPaginationState.page + 1);
    setPageSize(newPaginationState.pageSize);
  };

  const handleDelete = (inspectionPoint: InspectionPointDto) => {
    confirm({
      variant: 'danger',
      description: i18n._('Are you sure you want to remove this Inspection Point?'),
    }).then(() => {
      deleteInspectionPointMutation.mutate({
        projectId: projectId!,
        entityId: inspectionPoint.entityId,
      });
    });
  };

  const handleAssignSpool = (inspectionPoint: InspectionPointDto) => {
    if (!selectedSpool) return;
    assignInspectionPointSpoolMutation.mutate(
      { inspectionPointId: inspectionPoint.entityId, spoolId: selectedSpool.spoolId },
      { onSuccess: () => model.setSelectedSpool(undefined) },
    );
  };

  const handleInspectionPointSelect = (inspectionPointId: string) => {
    if (onInspectionPointSelect) {
      onInspectionPointSelect(inspectionPointId);
    } else {
      navigate(
        wearDataViewPath
          .replace(':entityId', projectId!)
          .replace(':inspectionPointId', inspectionPointId),
      );
    }
  };

  React.useEffect(() => {
    appliedInspectionPointSignatureRef.current = '';
  }, [projectId]);

  React.useEffect(() => {
    batchUpdateSpoolAppearanceRef.current = model.batchUpdateSpoolAppearance;
    setSpoolInspectionPointsRef.current = model.setSpoolInspectionPoints;
  }, [model.batchUpdateSpoolAppearance, model.setSpoolInspectionPoints]);

  // Update legend when pipe display mode changes
  React.useEffect(() => {
    if (pipeDisplayMode === null) {
      setShowLegend(false);
      setLegendData(null);
      return;
    }

    const maxOf = (values: Record<string, number> | undefined) => {
      const usable = Object.values(values ?? {}).filter((v) => v != null && Number.isFinite(v));
      return usable.length ? Math.max(...usable) : 0;
    };

    let legendData = null;

    switch (pipeDisplayMode) {
      case 'pipeType':
        legendData = generatePipeTypeLegend(model);
        break;
      case 'wearRate':
        if (pipeWear) legendData = generateWearRateLegend(0, maxOf(pipeWear));
        break;
      case 'throughputTonnes':
        legendData = generateThroughputLegend(0, maxOf(spoolTonnes));
        break;
      case 'remainingThickness':
        if (pipeWear) legendData = generateRemainingThicknessLegend(0, maxOf(pipeWear));
        break;
      case 'remainingPercentage':
        legendData = generateRemainingPercentageLegend();
        break;
      default:
        break;
    }

    if (legendData) {
      setLegendData(legendData);
      setShowLegend(true);
    }
  }, [pipeDisplayMode, model, pipeWear, spoolTonnes, setLegendData, setShowLegend]);

  React.useEffect(() => {
    const createdInspectionPointId = addInspectionPointMutation.data;
    if (!addInspectionPointMutation.isSuccess || !createdInspectionPointId) {
      return;
    }

    if (lastCreatedInspectionPointIdRef.current === createdInspectionPointId) {
      return;
    }

    lastCreatedInspectionPointIdRef.current = createdInspectionPointId;
    publishUndoAddInspectionPoint(createdInspectionPointId);
  }, [addInspectionPointMutation.data, addInspectionPointMutation.isSuccess, publishUndoAddInspectionPoint]);

  React.useEffect(() => {
    const spoolColourUpdates = Object.values(model.spools).map((spool) => ({
      spoolId: spool.spoolId,
      spoolColour: pipeDisplayMode === null ? SPOOL_COLOUR : pipeColours[spool.spoolId] ?? SPOOL_COLOUR,
    }));

    batchUpdateSpoolAppearanceRef.current(spoolColourUpdates);
  }, [pipeDisplayMode, pipeColours, model.spools]);

  // Keyed on the model rebuild too: initModelState recreates every spool without its inspection flag.
  React.useEffect(() => {
    if (data?.items) {
      const pointsSignature = data.items
        .map((item) => `${item.spoolId ?? ''}:${item.name ?? ''}`)
        .sort()
        .join('|');
      const signature = `${model.loadGeneration}#${pointsSignature}`;
      if (appliedInspectionPointSignatureRef.current === signature) return;
      appliedInspectionPointSignatureRef.current = signature;

      const inspectionPointsBySpoolId = data.items.reduce<Record<string, string>>((acc, item) => {
        if (!item.spoolId) return acc;
        acc[item.spoolId] = item.name ?? '';
        return acc;
      }, {});
      setSpoolInspectionPointsRef.current(inspectionPointsBySpoolId);
    }
  }, [data?.items, model.loadGeneration]);

  React.useEffect(() => {
    if (selectedSpool && data?.items) {
      const existingInspectionPoint = data.items.find((item) => item.spoolId === selectedSpool.spoolId);
      setDuplicateWarning(existingInspectionPoint
        ? `${i18n._('Spool')} ${describeSpool(selectedSpool)} ${i18n._('already has an inspection point')}`
        : null);
    } else {
      setDuplicateWarning(null);
    }
  }, [selectedSpool, data?.items]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{
        padding: 2,
      }}
      >

        {duplicateWarning && (
          <Alert severity="error" variant="filled">
            {duplicateWarning}
          </Alert>
        )}
        {!selectedSpool && !duplicateWarning && (
          <Box sx={{
            mb: 4,
          }}
          >
            <Paper elevation={0} variant="outlined" square>
              <Box
                sx={{
                  padding: 1,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body1" color="textSecondary">
                  <Trans id="Click on a spool to add an inspection point">Click on a spool to add an inspection point</Trans>
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {selectedSpool && !duplicateWarning && (
          <InspectionPointsForm
            projectId={projectId as string}
            mutation={addInspectionPointMutation}
            activeSpoolId={selectedSpool.spoolId}
            activeSpoolLabel={selectedSpoolLabel ?? selectedSpool.spoolId}
            activeSpoolName={selectedSpool.name}
            canEditSpools={canEditSpools}
            onSubmit={handleAddInspectionPoint}
            resetSpool={() => model.setSelectedSpool(undefined)}
          />
        )}

        <Typography variant="subtitle1" gutterBottom>
          <Trans id="Inspection Points">Inspection Points</Trans>
        </Typography>

        <InspectionPointsTable
          data={data?.items}
          page={page}
          pageSize={pageSize}
          totalItems={data?.pager.totalItems}
          loading={isFetching}
          loadingNew={isFetching && (!isFetched || isPreviousData)}
          onPaginationModelChange={handlePaginationChange}
          onDelete={handleDelete}
          describeInspectionPointSpool={describeInspectionPointSpool}
          resolveInspectionPointName={resolveInspectionPointName}
          assignSpoolLabel={selectedSpoolLabel}
          onAssignSpool={handleAssignSpool}
          canEditSpool={canEditInspectionPointSpool}
          onEditSpool={(inspectionPoint) => setSpoolToEditId(inspectionPoint.spoolId)}
          searchTerm=""
          onViewInspectionPointWearData={handleInspectionPointSelect}
        />
        {spoolToEditId && model.spools[spoolToEditId] && (
          <SpoolForm spool={model.spools[spoolToEditId]} model={model} onComplete={() => setSpoolToEditId(undefined)} />
        )}
      </Box>
      {/* Wear Monitoring Display Controls */}
      <Box sx={{
        padding: 2,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
      >
        <Typography variant="subtitle2" gutterBottom>
          <Trans id="Pipe Display Mode">Pipe Display Mode</Trans>
        </Typography>
        <ButtonGroup variant="outlined" size="small" fullWidth>
          <Button
            variant={getCurrentPipeDisplayMode() === 'remainingThickness' ? 'contained' : 'outlined'}
            onClick={() => handlePipeDisplayModeChange('remainingThickness')}
            sx={{ flex: 1 }}
            color="secondary"
          >
            <Trans id="Remaining Thickness">Remaining Thickness</Trans>
          </Button>
          <Button
            variant={getCurrentPipeDisplayMode() === 'remainingPercentage' ? 'contained' : 'outlined'}
            onClick={() => handlePipeDisplayModeChange('remainingPercentage')}
            sx={{ flex: 1 }}
            color="secondary"
          >
            <Trans id="Remaining %">Remaining %</Trans>
          </Button>
          <Button
            variant={getCurrentPipeDisplayMode() === 'wearRate' ? 'contained' : 'outlined'}
            onClick={() => handlePipeDisplayModeChange('wearRate')}
            sx={{ flex: 1 }}
            color="secondary"
          >
            <Trans id="Wear Rate">Wear Rate</Trans>
          </Button>
          <Button
            variant={getCurrentPipeDisplayMode() === 'throughputTonnes' ? 'contained' : 'outlined'}
            onClick={() => handlePipeDisplayModeChange('throughputTonnes')}
            sx={{ flex: 1 }}
            color="secondary"
          >
            <Trans id="Throughput">Throughput</Trans>
          </Button>
        </ButtonGroup>

        <Box sx={{ mt: 1 }}>
          <Button
            variant={getCurrentPipeDisplayMode() === null ? 'contained' : 'outlined'}
            onClick={() => handlePipeDisplayModeChange(null)}
            size="small"
            fullWidth
            color="secondary"
          >
            <Trans id="Default">Default</Trans>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default WearMonitoringPanelPage;
